import { useOAuthStatus } from './useOAuthStatus.ts';
// modules/social-accounts/AccountForm.tsx
// Панель: input Account Name → отримати OAuth URL → показати + скопіювати

import React, { useState, useId, useEffect } from 'react';
import { api, ApiClientError } from '../../core/apiClient.ts';
import type { OAuthUrlResponse } from '../../core/types.ts';
import { parseProxyString } from '../../core/proxyParser.ts';

interface AccountFormProps {
  /** Callback після успішного отримання URL (для показу нотифікацій) */
  onUrlGenerated?: (url: string) => void;
}

export function AccountForm({ onUrlGenerated }: AccountFormProps) {
  const inputId = useId();

  const [accountName, setAccountName] = useState('');
  const [proxy, setProxy] = useState({ host: '', port: '', username: '', password: '', protocol: 'http' as 'http' | 'socks5' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [oauthResult, setOauthResult] = useState<OAuthUrlResponse | null>(null);
  const [copied, setCopied] = useState(false);
  const oauthStatus = useOAuthStatus(oauthResult?.sessionId, oauthResult?.expiresAt);

  useEffect(() => {
    if (oauthStatus?.status === 'success') window.dispatchEvent(new Event('accounts-updated'));
  }, [oauthStatus]);

  const applyParsedProxy = (parsed: ReturnType<typeof parseProxyString>) => {
    if (!parsed) return false;
    setProxy((prev) => ({
      ...prev,
      host: parsed.host,
      port: parsed.port !== undefined && parsed.port !== '' ? parsed.port : prev.port,
      username: parsed.username !== undefined ? parsed.username : prev.username,
      password: parsed.password !== undefined ? parsed.password : prev.password,
      protocol: parsed.protocol ?? prev.protocol,
    }));
    return true;
  };

  const handleProxyHostPaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    const text = e.clipboardData.getData('text');
    const parsed = parseProxyString(text);
    if (parsed) {
      e.preventDefault();
      applyParsedProxy(parsed);
    }
  };

  const handleProxyHostChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    const parsed = parseProxyString(val);
    if (parsed && (parsed.port || parsed.username || parsed.password || parsed.protocol)) {
      applyParsedProxy(parsed);
    } else {
      setProxy((prev) => ({ ...prev, host: val }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!accountName.trim()) {
      setError('Введіть ім\'я акаунту');
      return;
    }
    const hasProxy = [proxy.host, proxy.port, proxy.username, proxy.password].some((value) => value.trim() !== '');
    if (hasProxy && (!proxy.host.trim() || !proxy.port.trim() || !proxy.username.trim() || !proxy.password.trim())) {
      setError('Заповніть усі поля проксі або залиште їх порожніми для прямого підключення');
      return;
    }

    setLoading(true);
    setError(null);
    setOauthResult(null);
    setCopied(false);

    try {
      const result = await api.post<OAuthUrlResponse>('/api/accounts/oauth-url', {
        accountName: accountName.trim(),
        ...(hasProxy ? { proxy: { ...proxy, port: Number(proxy.port) } } : {}),
      });
      setOauthResult(result);
      
      // Автоматичне копіювання
      try {
        await navigator.clipboard.writeText(result.url);
        setCopied(true);
      } catch {
        const ta = document.createElement('textarea');
        ta.value = result.url;
        document.body.appendChild(ta);
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
        setCopied(true);
      }
      
      onUrlGenerated?.(result.url);
    } catch (err) {
      const message = err instanceof ApiClientError
        ? err.message
        : 'Не вдалося отримати OAuth URL. Перевірте налаштування Worker.';
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setOauthResult(null);
    setAccountName('');
    setProxy({ host: '', port: '', username: '', password: '', protocol: 'http' });
    setError(null);
    setCopied(false);
  };

  return (

    <div className="form-panel glass-card">
      {oauthStatus && <div role="status" className={`notification notification--${oauthStatus.status === 'success' ? 'success' : ['error', 'expired'].includes(oauthStatus.status) ? 'error' : 'info'}`}>
        {oauthStatus.message ?? 'Очікуємо авторизацію у браузері акаунта…'}
      </div>}
      <div className="flex items-center gap-3" style={{ marginBottom: 'var(--space-4)' }}>
        <div>
          <h3 className="form-panel__title" style={{ marginBottom: 0 }}>
            Підключити акаунт
          </h3>
          <p className="text-xs text-muted" style={{ marginTop: 2 }}>
            Проксі необов'язковий. Залиште всі його поля порожніми для прямого підключення Worker до Meta.
          </p>
        </div>
      </div>

      <form onSubmit={handleSubmit}>
        <div className="form-row">
          <div className="field">
            <label htmlFor={inputId} className="label">
              Ім'я акаунту
            </label>
            <input
              id={inputId}
              type="text"
              className="input"
              placeholder="Наприклад: John Doe"
              value={accountName}
              onChange={(e) => setAccountName(e.target.value)}
              disabled={loading || !!oauthResult}
              autoComplete="off"
              spellCheck={false}
            />
          </div>
          <div className="field">
            <label className="label">Proxy host</label>
            <input
              className="input"
              placeholder="IP / host або рядок проксі"
              title="Вставте IP:порт або повний рядок (http://ip:port:user:pass@) для автопарсингу"
              value={proxy.host}
              onChange={handleProxyHostChange}
              onPaste={handleProxyHostPaste}
              disabled={loading || !!oauthResult}
            />
          </div>
          <div className="field">
            <label className="label">Port</label>
            <input
              className="input"
              type="number"
              placeholder="8080"
              value={proxy.port}
              onChange={(e) => setProxy({ ...proxy, port: e.target.value })}
              disabled={loading || !!oauthResult}
            />
          </div>
          <div className="field">
            <label className="label">Proxy user</label>
            <input
              className="input"
              placeholder="user"
              value={proxy.username}
              onChange={(e) => setProxy({ ...proxy, username: e.target.value })}
              disabled={loading || !!oauthResult}
            />
          </div>
          <div className="field">
            <label className="label">Proxy password</label>
            <input
              className="input"
              type="password"
              placeholder="password"
              value={proxy.password}
              onChange={(e) => setProxy({ ...proxy, password: e.target.value })}
              disabled={loading || !!oauthResult}
            />
          </div>
          <div className="field">
            <label className="label">Protocol</label>
            <select
              className="input"
              value={proxy.protocol}
              onChange={(e) => setProxy({ ...proxy, protocol: e.target.value as 'http' | 'socks5' })}
              disabled={loading || !!oauthResult}
            >
              <option value="http">HTTP CONNECT</option>
              <option value="socks5">SOCKS5</option>
            </select>
          </div>
          <button
            type="submit"
            className={`btn btn--primary${loading ? ' btn--loading' : ''}`}
            disabled={loading || !!oauthResult}
            style={{ height: 44, alignSelf: 'flex-end' }}
          >
            {loading ? 'Завантаження...' : 'Отримати URL'}
          </button>
        </div>
      </form>

      {/* Помилка */}
      {error && (
        <div className="notification notification--error" style={{ marginTop: 'var(--space-4)' }}>
          <span>{error}</span>
        </div>
      )}

      {/* Результат — OAuth URL */}
      {oauthResult && (
        <div style={{ marginTop: 'var(--space-4)' }}>
          <div style={{ marginBottom: 'var(--space-2)', fontWeight: 500, color: 'var(--color-success)' }}>
            {copied ? 'URL автоматично скопійовано!' : 'URL згенеровано!'}
          </div>
          <div className="flex gap-2 items-center">
            <input 
              type="text" 
              className="input" 
              value={oauthResult.url} 
              readOnly 
              style={{ flex: 1, fontSize: '0.85rem' }} 
              onClick={(e) => (e.target as HTMLInputElement).select()}
            />
            <button
              type="button"
              className="btn btn--secondary"
              onClick={handleReset}
            >
              Новий акаунт
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
